import client from './client';

export type SubscriptionTier = 'free' | 'pro';
export type SubscriptionStatus = 'active' | 'trial' | 'canceled';
export type BillingCycle = 'monthly' | 'annual';

export interface SubscriptionSnapshot {
    tier: SubscriptionTier;
    status: SubscriptionStatus;
    trial_start?: string | null;
    trial_end?: string | null;
    trial_days_left?: number | null;
    stripe_customer_id?: string | null;
    stripe_subscription_id?: string | null;
    cancel_at_period_end?: boolean;
    cancel_at?: string | null;
    access_until?: string | null;
    can_resume?: boolean;
}

export interface EntitlementSnapshot {
    plan_tier: SubscriptionTier;
    included_mailboxes: number;
    additional_mailboxes_count: number;
    mailbox_limit?: number | null;
    connected_mailboxes: number;
    included_ai_credits_monthly: number;
    ai_credits_used: number;
    ai_credits_remaining: number;
    included_pages_monthly?: number;
    pages_used?: number;
    pages_remaining?: number;
    max_pages_per_document?: number;
    current_period_start?: string | null;
    current_period_end?: string | null;
    reset_date?: string | null;
    ai_credit_event_type: string;
    mailbox_addon_price_cents: number;
    allow_mailbox_addons: boolean;
    allow_credit_packs: boolean;
    /** Server-driven: account may run new deep analysis / document Q&A (not a Stripe tier flag). */
    analytical_enabled?: boolean;
}

export interface BillingInvoice {
    id: string;
    date?: string | null;
    amount_paid: number;
    amount_due: number;
    currency: string;
    status: string;
    hosted_invoice_url?: string | null;
    invoice_pdf?: string | null;
}

export const subscriptionApi = {
    getStatus: async (): Promise<SubscriptionSnapshot> => {
        const res = await client.get('/api/subscription/status');
        return res.data?.subscription;
    },
    getStatusBundle: async (): Promise<{ subscription: SubscriptionSnapshot; entitlements?: EntitlementSnapshot | null }> => {
        const res = await client.get('/api/subscription/status');
        return {
            subscription: res.data?.subscription,
            entitlements: res.data?.entitlements || null,
        };
    },
    getEntitlements: async (): Promise<EntitlementSnapshot | null> => {
        const res = await client.get('/api/billing/entitlements');
        return res.data?.entitlements || null;
    },
    startTrial: async (optInTrial: boolean = true): Promise<SubscriptionSnapshot> => {
        const res = await client.post('/api/subscription/create', {
            action: 'trial',
            opt_in_trial: optInTrial,
        });
        return res.data?.subscription;
    },
    createCheckout: async (planCycle: BillingCycle, successUrl?: string, cancelUrl?: string): Promise<{ checkout_session_id?: string; checkout_url?: string; subscription: SubscriptionSnapshot; }> => {
        const res = await client.post('/api/subscription/update', {
            plan_cycle: planCycle,
            success_url: successUrl,
            cancel_url: cancelUrl,
        });
        return res.data;
    },
    cancel: async (cancelImmediately = false): Promise<SubscriptionSnapshot> => {
        const res = await client.post('/api/subscription/cancel', { cancel_immediately: cancelImmediately });
        return res.data?.subscription;
    },
    resume: async (): Promise<SubscriptionSnapshot> => {
        const res = await client.post('/api/subscription/resume');
        return res.data?.subscription;
    },
    getBillingHistory: async (limit: number = 20): Promise<BillingInvoice[]> => {
        const res = await client.get('/api/billing/history', { params: { limit } });
        return res.data?.invoices || [];
    },
    createBillingPortal: async (returnUrl?: string): Promise<{ portal_url?: string | null }> => {
        const res = await client.post('/api/billing/portal', { return_url: returnUrl });
        return { portal_url: res.data?.portal_url };
    },
};

export default subscriptionApi;
